import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Home, ChevronDown, MapPin, Building2, Landmark, ArrowRight } from "lucide-react";
import { getNavData, getRegions } from "@/lib/api";
import { T } from "@/context/LanguageContext";

const MENUS = [
  { label: "States", type: "states", to: "/explore/states" },
  { label: "Regions", type: "regions", to: "/explore/states?group=historical" },
  { label: "Territories", type: "territories", to: "/explore/states?type=territory" },
  { label: "Counties", type: "counties", to: "/explore/states" },
  { label: "Cities & Towns", type: "cities", to: "/explore/states" },
  { label: "Atlas", to: "/atlas" },
  { label: "People", to: "/people" },
  { label: "History", to: "/vertical/history" },
  { label: "Culture", to: "/vertical/culture" },
  { label: "Government", to: "/vertical/government" },
  { label: "Economy", to: "/vertical/economy" },
  { label: "More", type: "more", to: "/" },
];

const MORE_LINKS = [
  { g: "Discover", items: [["Tourism", "/vertical/tourism"], ["Nature", "/vertical/nature"], ["Healthcare", "/vertical/healthcare"], ["Education", "/vertical/education"], ["Science", "/vertical/science"], ["Agriculture", "/vertical/agriculture"], ["Places", "/vertical/places"]] },
  { g: "Explore", items: [["Interactive Atlas", "/atlas"], ["Notable People", "/people"], ["All States", "/explore/states"], ["Compare Symbols", "/symbols/compare"], ["Flags of America", "/flags"]] },
];

export function NavMega() {
  const [data, setData] = useState(null);
  const [regions, setRegions] = useState([]);
  const [active, setActive] = useState(null);
  const timer = useRef(null);

  useEffect(() => { getNavData().then(setData).catch(() => {}); }, []);
  useEffect(() => { getRegions().then((d) => setRegions(d.regions)).catch(() => {}); }, []);

  const open = (label) => { if (timer.current) clearTimeout(timer.current); setActive(label); };
  const close = () => { timer.current = setTimeout(() => setActive(null), 160); };

  const activeMenu = MENUS.find((m) => m.label === active && m.type);

  return (
    <nav className="relative bg-navy text-white shadow-md" onMouseLeave={close} data-testid="primary-nav">
      <div className="mx-auto flex max-w-[1500px] items-stretch px-2">
        <Link to="/" className="flex items-center bg-oldglory-red px-4 py-3 transition-colors hover:bg-oldglory-dark" data-testid="nav-home">
          <Home className="h-4 w-4" />
        </Link>
        <div className="no-scrollbar flex flex-1 items-stretch overflow-x-auto">
          {MENUS.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              onMouseEnter={() => open(n.label)}
              className={`flex items-center gap-1 whitespace-nowrap px-3.5 py-3 text-[13px] font-semibold uppercase tracking-wide transition-colors hover:bg-navy-800 ${active === n.label ? "bg-navy-800 text-white" : "text-white/90"}`}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              <T>{n.label}</T>
              {n.type && <ChevronDown className={`h-3 w-3 opacity-70 transition-transform ${active === n.label ? "rotate-180" : ""}`} />}
            </Link>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {activeMenu && data && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            onMouseEnter={() => open(activeMenu.label)}
            className="absolute left-0 right-0 top-full z-50 border-t-2 border-oldglory-red bg-white text-ink shadow-2xl"
            data-testid={`megamenu-${activeMenu.type}`}
          >
            <div className="mx-auto max-h-[72vh] max-w-[1500px] overflow-y-auto px-6 py-6">
              {activeMenu.type === "states" && <Grid items={data.states} icon={Landmark} viewAll="/explore/states" cols="grid-cols-3 md:grid-cols-5 lg:grid-cols-6" onNavigate={() => setActive(null)} />}
              {activeMenu.type === "regions" && (
                <div data-testid="megamenu-regions-panel">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-oldglory-red"><T>Historical Regions</T></div>
                    <Link to="/explore/states?group=historical" onClick={() => setActive(null)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid="megamenu-regions-all"><T>All regions</T> <ArrowRight className="h-3.5 w-3.5" /></Link>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3 lg:grid-cols-5">
                    {regions.map((r) => (
                      <Link key={r.key} to={`/region/${r.key}`} onClick={() => setActive(null)} className="group flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors hover:bg-parchment" data-testid={`nav-region-${r.key}`}>
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-oldglory-red/70" />
                        <span className="truncate text-ink group-hover:text-navy">{r.label}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground">{r.state_count}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {activeMenu.type === "territories" && <Grid items={data.territories} icon={Landmark} viewAll="/explore/states?type=territory" cols="grid-cols-2 md:grid-cols-3" onNavigate={() => setActive(null)} />}
              {activeMenu.type === "counties" && <Grid items={data.counties} icon={Building2} viewAll="/explore/states" cols="grid-cols-2 md:grid-cols-3 lg:grid-cols-4" showParent onNavigate={() => setActive(null)} />}
              {activeMenu.type === "cities" && <CitiesByState groups={data.cities_by_state} onNavigate={() => setActive(null)} />}
              {activeMenu.type === "more" && (
                <div className="grid gap-8 md:grid-cols-2">
                  {MORE_LINKS.map((col) => (
                    <div key={col.g}>
                      <div className="mb-3 text-[10px] font-bold uppercase tracking-widest text-oldglory-red"><T>{col.g}</T></div>
                      <div className="grid grid-cols-2 gap-1">
                        {col.items.map(([label, to]) => (
                          <Link key={label} to={to} onClick={() => setActive(null)} className="rounded px-2 py-1.5 text-sm text-ink transition-colors hover:bg-parchment hover:text-navy" data-testid={`more-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                            <T>{label}</T>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

function CitiesByState({ groups, onNavigate }) {
  if (!groups || !groups.length) return null;
  return (
    <div data-testid="cities-by-state">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-widest text-oldglory-red">
          <T>Cities & Towns — every state</T>
        </div>
        <Link to="/explore/states" onClick={onNavigate} className="inline-flex items-center gap-1.5 text-xs font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid="megamenu-view-all">
          <T>All states</T> <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="gap-x-8 [column-fill:_balance] sm:columns-2 lg:columns-3 xl:columns-4">
        {groups.map((g) => (
          <div key={g.state_slug} className="mb-5 break-inside-avoid" data-testid={`cities-state-${g.state_slug}`}>
            <Link
              to={`/place${g.state_url_path}`}
              onClick={onNavigate}
              className="mb-1 flex items-center gap-1.5 text-[13px] font-bold text-navy hover:text-oldglory-red"
              data-testid={`cities-state-header-${g.state_slug}`}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-oldglory-red/70" />
              {g.state_name}
            </Link>
            <div className="flex flex-col">
              {g.cities.map((c) => (
                <Link
                  key={c.geo_id}
                  to={`/place${c.url_path}`}
                  onClick={onNavigate}
                  className="truncate rounded px-1.5 py-0.5 text-[13px] text-ink transition-colors hover:bg-parchment hover:text-navy"
                  data-testid={`nav-place-${c.geo_id}`}
                >
                  {c.name}
                </Link>
              ))}
              <Link
                to={g.all_url}
                onClick={onNavigate}
                className="mt-0.5 inline-flex items-center gap-1 px-1.5 text-[11px] font-semibold text-oldglory-red hover:text-oldglory-dark"
                data-testid={`cities-state-all-${g.state_slug}`}
              >
                <T>See all</T> <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Grid({ items, icon: Icon, viewAll, cols, showParent, onNavigate }) {
  const parentName = (pid) => (pid ? pid.split("__")[0].replace(/-/g, " ") : "");
  return (
    <div>
      <div className={`grid ${cols} gap-x-6 gap-y-1`}>
        {items.map((it) => (
          <Link key={it.geo_id} to={`/place${it.url_path}`} onClick={onNavigate} className="group flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors hover:bg-parchment" data-testid={`nav-place-${it.geo_id}`}>
            <Icon className="h-3.5 w-3.5 shrink-0 text-oldglory-red/70" />
            <span className="truncate text-ink group-hover:text-navy">{it.name}</span>
            {showParent && <span className="ml-auto truncate text-[10px] capitalize text-muted-foreground">{parentName(it.parent_id)}</span>}
          </Link>
        ))}
      </div>
      <Link to={viewAll} onClick={onNavigate} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-oldglory-red hover:text-oldglory-dark" data-testid="megamenu-view-all">
        <T>View all</T> <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
